/**
 * 003 — Liquiditätsprognose (US6, FR-050): day-by-day projected balance from
 * the latest statement-backed balance. Every projected line carries its source
 * ("wiederkehrend (geschätzt)", "erwartet", "geplant"); the first day below
 * zero is highlighted. Without a backed balance up to the end of last month
 * the forecast refuses and names the missing statement period.
 *
 * Planned items are planning data only (never actuals). GF and Stbr may both
 * maintain them (FR-060), so this tab does not use `canManage`.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, GlassPanel, Input, LoadingSpinner, StatusBadge, type StatusTone } from '../ui';
import {
  bankApi,
  bankKeys,
  day,
  ibanLabel,
  money,
  type ForecastItem,
  type ForecastResponse,
  type ForecastSource,
  type PlannedItem,
  type PlannedItemBody,
  type RecurringForecastGroup,
} from '../../lib/bank-api';
import { errorText } from './ReviewEditor';
import type { BankTabProps } from './types';

const HORIZONS = [30, 60, 90, 180, 365] as const;
const SOURCE_TONE: Record<ForecastSource, StatusTone> = { recurring: 'neutral', invoice: 'info', planned: 'warning' };

/** Exact cents from "1.234,56", "1234,5" or "1234.56" (no float rounding); null if unreadable. */
export function parseEuroToCents(input: string): number | null {
  const s = input.replace(/[\s€]/g, '');
  let whole: string, frac: string;
  if (s.includes(',')) {
    const m = s.match(/^(\d{1,3}(?:\.\d{3})*|\d+),(\d{1,2})$|^(\d{1,3}(?:\.\d{3})+|\d+)$/);
    if (!m) return null;
    whole = (m[1] ?? m[3] ?? '').replace(/\./g, '');
    frac = m[2] ?? '';
  } else if (/^\d{1,3}(?:\.\d{3})+$/.test(s)) {
    // "5.000" is German thousands grouping, not a decimal point.
    whole = s.replace(/\./g, '');
    frac = '';
  } else {
    const m = s.match(/^(\d+)(?:\.(\d{1,2}))?$/);
    if (!m) return null;
    whole = m[1]!;
    frac = m[2] ?? '';
  }
  return Number(whole) * 100 + Number(frac.padEnd(2, '0') || '0');
}

const centsToInput = (cents: number) => (Math.abs(cents) / 100).toFixed(2).replace('.', ',');

function SourceBadge({ item }: { item: ForecastItem }) {
  return (
    <StatusBadge tone={SOURCE_TONE[item.source]} data-testid="forecast-source">
      {item.sourceLabel}
      {item.overdue ? ' · überfällig' : ''}
    </StatusBadge>
  );
}

function Refused({ data }: { data: Extract<ForecastResponse, { ok: false }> }) {
  return (
    <GlassPanel role="alert" data-testid="forecast-refused">
      <h2 className="text-lg font-semibold">Keine Prognose möglich</h2>
      <p className="mt-2 text-sm">
        Die Prognose startet nur von einem durch Kontoauszüge belegten Saldo bis mindestens {day(data.cutoff)}. Es fehlt:
      </p>
      <ul className="mt-2 text-sm text-amber-200">
        {data.missing.map((m) => (
          <li key={`${m.account}:${m.from}`} data-testid="forecast-missing">
            {m.account === null
              ? `Noch kein Kontoauszug importiert (benötigt bis ${day(m.to)})`
              : `${ibanLabel(m.account)}: Auszug ${day(m.from)} – ${day(m.to)}`}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-sm text-white/70">Bitte den fehlenden Auszug oben unter „Originalauszüge importieren" einlesen.</p>
    </GlassPanel>
  );
}

function Projection({ data }: { data: Extract<ForecastResponse, { ok: true }> }) {
  const [showAllDays, setShowAllDays] = useState(false);
  const rows = showAllDays ? data.days : data.days.filter((d) => d.items.length > 0 || d.date === data.firstNegative?.date);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <GlassPanel>
          <p className="text-sm text-white/60">Belegter Saldo am {day(data.anchor.date)}</p>
          <p className="text-xl font-semibold" data-testid="forecast-start">
            {money(data.anchor.balanceCents)}
          </p>
        </GlassPanel>
        <GlassPanel>
          <p className="text-sm text-white/60">Tiefster Stand</p>
          <p className="text-xl font-semibold" data-testid="forecast-lowest">
            {data.lowest ? `${money(data.lowest.balanceCents)} am ${day(data.lowest.date)}` : '—'}
          </p>
        </GlassPanel>
        <GlassPanel>
          <p className="text-sm text-white/60">Saldo am {day(data.endDate)}</p>
          <p className="text-xl font-semibold">{money(data.endCents)}</p>
        </GlassPanel>
      </div>
      {data.firstNegative ? (
        <p role="alert" data-testid="forecast-first-negative" className="rounded border border-red-400/60 bg-red-500/10 p-3 font-medium text-red-200">
          Erstmals unter null am {day(data.firstNegative.date)}: {money(data.firstNegative.balanceCents)}
        </p>
      ) : (
        <p data-testid="forecast-no-negative" className="text-sm text-white/70">
          Im Zeitraum bis {day(data.endDate)} kein Tag unter null.
        </p>
      )}
      {!data.includeExpected && data.excludedExpected.count > 0 && (
        <p className="text-sm text-white/70" data-testid="forecast-excluded-expected">
          Vorsichtige Sicht: {data.excludedExpected.count} erwartete Zahlungseingänge ({money(data.excludedExpected.cents)}) nicht
          eingerechnet.
        </p>
      )}
      <GlassPanel>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">Verlauf</h2>
          <label className="text-sm">
            <input type="checkbox" checked={showAllDays} onChange={(e) => setShowAllDays(e.target.checked)} /> Alle Tage zeigen
          </label>
        </div>
        {rows.length === 0 ? (
          <EmptyState title="Keine bekannten Zahlungen im Zeitraum." />
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left">Datum</th>
                  <th className="text-left">Position</th>
                  <th className="text-left">Quelle</th>
                  <th className="text-right">Betrag</th>
                  <th className="text-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const negative = d.date === data.firstNegative?.date;
                  const cls = negative ? 'bg-red-500/15 text-red-100' : d.balanceCents < 0 ? 'text-red-200' : '';
                  if (d.items.length === 0)
                    return (
                      <tr key={d.date} className={cls} data-testid="forecast-day">
                        <td>{day(d.date)}</td>
                        <td colSpan={3} className="text-white/50">
                          keine Bewegung
                        </td>
                        <td className="text-right">{money(d.balanceCents)}</td>
                      </tr>
                    );
                  return d.items.map((i, idx) => (
                    <tr
                      key={`${d.date}:${i.source}:${i.refId}:${idx}`}
                      className={cls}
                      data-testid={negative ? 'forecast-row-first-negative' : 'forecast-row'}
                    >
                      <td>{idx === 0 ? day(d.date) : ''}</td>
                      <td className="break-words">{i.label}</td>
                      <td>
                        <SourceBadge item={i} />
                      </td>
                      <td className="text-right">{money(i.amountCents)}</td>
                      <td className="text-right font-medium">{idx === d.items.length - 1 ? money(d.balanceCents) : ''}</td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>
      {(data.notCounted.plannedBeforeAnchor.length > 0 || data.notCounted.invoicesWithoutDueDate.length > 0) && (
        <GlassPanel data-testid="forecast-not-counted">
          <h2 className="font-semibold">Nicht eingerechnet</h2>
          <ul className="mt-2 text-sm text-white/80">
            {data.notCounted.plannedBeforeAnchor.map((i) => (
              <li key={i.refId}>
                Geplant {day(i.date)} „{i.label}" ({money(i.amountCents)}): liegt vor dem belegten Saldo vom {day(data.anchor.date)}
              </li>
            ))}
            {data.notCounted.invoicesWithoutDueDate.map((i) => (
              <li key={i.id}>
                Rechnung {i.number ?? '—'} ({money(i.remainingCents)}): ohne Fälligkeitsdatum
              </li>
            ))}
          </ul>
        </GlassPanel>
      )}
      {data.longOverdue.count > 0 && (
        <GlassPanel data-testid="forecast-long-overdue">
          <h2 className="font-semibold">
            {data.longOverdue.included ? 'Lange überfällig, eingerechnet' : 'Überfällig, nicht eingeplant'} (mehr als 60 Tage)
          </h2>
          <p className="mt-1 text-sm text-white/70">
            {data.longOverdue.count} Rechnungen, zusammen {money(data.longOverdue.cents)}.{' '}
            {data.longOverdue.included
              ? 'Sie stehen als „erwartet" am ersten Prognosetag.'
              : 'Sie zählen nicht als erwarteter Eingang; oben lassen sie sich einrechnen.'}
          </p>
          <ul className="mt-2 text-sm text-white/80">
            {data.longOverdue.invoices.map((i) => (
              <li key={i.id}>
                {i.label}: {money(i.remainingCents)}, fällig seit {day(i.dueDate)}
              </li>
            ))}
          </ul>
        </GlassPanel>
      )}
    </>
  );
}

const INTERVAL_LABELS: Record<RecurringForecastGroup['interval'], string> = {
  monthly: 'monatlich',
  quarterly: 'quartalsweise',
  yearly: 'jährlich',
};

function EndForm({
  group,
  today,
  pending,
  onSubmit,
  onCancel,
}: {
  group: RecurringForecastGroup;
  today: string;
  pending: boolean;
  onSubmit: (endedFrom: string, reason: string) => void;
  onCancel: () => void;
}) {
  const [endedFrom, setEndedFrom] = useState(group.nextDate ?? today);
  const [reason, setReason] = useState('');
  const valid = Boolean(endedFrom) && reason.trim().length >= 3;
  return (
    <form
      className="mt-2 flex flex-wrap items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !pending) onSubmit(endedFrom, reason.trim());
      }}
    >
      <label className="text-sm">
        Beendet ab
        <Input type="date" aria-label="Beendet ab" value={endedFrom} onChange={(e) => setEndedFrom(e.target.value)} />
      </label>
      <label className="text-sm">
        Grund
        <Input aria-label="Grund" placeholder="z. B. gekündigt" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <Button type="submit" disabled={!valid || pending}>
        Beenden
      </Button>
      <p className="basis-full text-xs text-white/60">
        Datum: ab wann keine Lastschrift mehr kommt. Bucht die Bank danach weiter regelmäßig ab, gilt das Ende nicht.
      </p>
      <Button type="button" onClick={onCancel}>
        Abbrechen
      </Button>
    </form>
  );
}

/** Detected recurring series; each can be ended from a date (e.g. a cancelled subscription) or resumed. */
function RecurringGroups({ data, onNotice }: { data: Extract<ForecastResponse, { ok: true }>; onNotice: (m: string) => void }) {
  const client = useQueryClient();
  const [ending, setEnding] = useState<string | null>(null);
  const refresh = () => void client.invalidateQueries({ queryKey: ['bank', 'forecast'] });
  const end = useMutation({
    mutationFn: (body: { groupKey: string; label: string; amountCents: number; endedFrom: string; reason: string }) => bankApi.endRecurring(body),
    onSuccess: () => {
      setEnding(null);
      onNotice('Wiederkehrende Lastschrift beendet; sie wird ab dem Datum nicht mehr fortgeschrieben.');
      refresh();
    },
    onError: (e) => onNotice(errorText(e)),
  });
  const resume = useMutation({
    mutationFn: (id: string) => bankApi.resumeRecurring(id),
    onSuccess: () => {
      onNotice('Wiederkehrende Lastschrift wird wieder fortgeschrieben.');
      refresh();
    },
    onError: (e) => onNotice(errorText(e)),
  });
  if (data.recurringGroups.length === 0 && data.unmatchedEndings.length === 0) return null;
  return (
    <GlassPanel data-testid="forecast-recurring-groups">
      <h2 className="font-semibold">Wiederkehrende Lastschriften (geschätzt)</h2>
      <p className="mt-1 text-sm text-white/70">
        Aus den Kontoauszügen erkannt. Ist ein Vertrag gekündigt, hier „Beenden" wählen: ab dem Datum wird nichts mehr
        fortgeschrieben (protokolliert, jederzeit rücknehmbar).
      </p>
      <ul className="mt-3 divide-y divide-white/10">
        {data.recurringGroups.map((g) => (
          <li key={g.refId} className="py-2" data-testid="forecast-recurring-group">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 break-words">
                <strong>{g.label}</strong> · {INTERVAL_LABELS[g.interval]} · {money(g.amountCents)} · zuletzt {day(g.lastDate)}
                {g.ended ? (
                  <span className="block text-sm text-amber-200" data-testid="forecast-recurring-ended">
                    beendet ab {day(g.ended.from)}: {g.ended.reason}
                  </span>
                ) : (
                  <span className="block text-sm text-white/60">{g.nextDate ? `nächste ${day(g.nextDate)}` : 'im Zeitraum keine weitere'}</span>
                )}
              </span>
              {g.ended ? (
                <Button disabled={resume.isPending} onClick={() => resume.mutate(g.ended!.id)}>
                  Wieder aufnehmen
                </Button>
              ) : (
                ending !== g.refId && <Button onClick={() => setEnding(g.refId)}>Beenden…</Button>
              )}
            </div>
            {ending === g.refId && !g.ended && (
              <EndForm
                group={g}
                today={data.today}
                pending={end.isPending}
                onSubmit={(endedFrom, reason) => end.mutate({ groupKey: g.groupKey, label: g.label, amountCents: g.amountCents, endedFrom, reason })}
                onCancel={() => setEnding(null)}
              />
            )}
          </li>
        ))}
        {data.unmatchedEndings.map((e) => (
          <li key={e.id} className="py-2" data-testid="forecast-recurring-unmatched">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 break-words">
                <strong>{e.label ?? e.groupKey}</strong>
                {e.amountCents !== null ? ` · ${money(e.amountCents)}` : ''}
                <span className="block text-sm text-amber-200">
                  beendet ab {day(e.endedFrom)}: {e.reason} · derzeit keine laufende Lastschrift erkannt
                </span>
              </span>
              <Button disabled={resume.isPending} onClick={() => resume.mutate(e.id)}>
                Wieder aufnehmen
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </GlassPanel>
  );
}

type Draft = { plannedDate: string; amount: string; direction: 'in' | 'out'; label: string; counterparty: string };
const emptyDraft = (): Draft => ({ plannedDate: '', amount: '', direction: 'out', label: '', counterparty: '' });

function PlannedItemForm({
  initial,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  initial: Draft;
  submitLabel: string;
  pending: boolean;
  /** Resolves true when saved; the add form clears only then (Codex #2). */
  onSubmit: (body: PlannedItemBody) => Promise<boolean>;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const cents = parseEuroToCents(draft.amount);
  const valid = Boolean(draft.plannedDate) && cents !== null && cents > 0 && draft.label.trim().length > 0;
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || pending) return;
        void onSubmit({
          plannedDate: draft.plannedDate,
          amountCents: draft.direction === 'out' ? -cents! : cents!,
          label: draft.label.trim(),
          counterparty: draft.counterparty.trim() || null,
        }).then((saved) => {
          if (saved && !onCancel) setDraft(emptyDraft());
        });
      }}
    >
      <label className="text-sm">
        Datum
        <Input type="date" aria-label="Datum" value={draft.plannedDate} onChange={(e) => set({ plannedDate: e.target.value })} />
      </label>
      <label className="text-sm">
        Richtung
        <select
          aria-label="Richtung"
          className="block rounded border border-white/10 bg-surface p-2"
          value={draft.direction}
          onChange={(e) => set({ direction: e.target.value as Draft['direction'] })}
        >
          <option value="out">Ausgang</option>
          <option value="in">Eingang</option>
        </select>
      </label>
      <label className="text-sm">
        Betrag (€)
        <Input aria-label="Betrag" inputMode="decimal" placeholder="1.234,56" value={draft.amount} onChange={(e) => set({ amount: e.target.value })} />
      </label>
      <label className="text-sm">
        Bezeichnung
        <Input aria-label="Bezeichnung" placeholder="z. B. USt-VA Q3" value={draft.label} onChange={(e) => set({ label: e.target.value })} />
      </label>
      <label className="text-sm">
        Gegenpartei (optional)
        <Input aria-label="Gegenpartei" value={draft.counterparty} onChange={(e) => set({ counterparty: e.target.value })} />
      </label>
      <Button type="submit" disabled={!valid || pending}>
        {submitLabel}
      </Button>
      {onCancel && (
        <Button type="button" onClick={onCancel}>
          Abbrechen
        </Button>
      )}
    </form>
  );
}

function PlannedItems({ tenantId, onNotice }: Pick<BankTabProps, 'tenantId' | 'onNotice'>) {
  const client = useQueryClient();
  // The item as it was when editing started: its version guards the save even if the
  // list refetches meanwhile, so a concurrent change yields 409 instead of an overwrite.
  const [editing, setEditing] = useState<PlannedItem | null>(null);
  const list = useQuery({
    queryKey: bankKeys.plannedItems(tenantId),
    queryFn: () => bankApi.plannedItems(),
    enabled: Boolean(tenantId),
  });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['bank', 'planned-items'] });
    void client.invalidateQueries({ queryKey: ['bank', 'forecast'] });
  };
  const create = useMutation({
    mutationFn: (body: PlannedItemBody) => bankApi.createPlannedItem(body),
    onSuccess: () => {
      onNotice('Geplante Position gespeichert.');
      refresh();
    },
    onError: (e) => onNotice(errorText(e)),
  });
  const update = useMutation({
    mutationFn: ({ item, body }: { item: PlannedItem; body: PlannedItemBody }) =>
      bankApi.updatePlannedItem(item.id, { ...body, expectedVersion: item.version }),
    onSuccess: () => {
      setEditing(null);
      onNotice('Geplante Position geändert.');
      refresh();
    },
    onError: (e) => onNotice(errorText(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => bankApi.deletePlannedItem(id),
    onSuccess: () => {
      onNotice('Geplante Position gelöscht.');
      refresh();
    },
    onError: (e) => onNotice(errorText(e)),
  });
  return (
    <GlassPanel data-testid="planned-items">
      <h2 className="font-semibold">Geplante Positionen</h2>
      <p className="mt-1 text-sm text-white/70">
        Erwartete Zahlungen ohne Beleg (geplante Rechnungen, Entnahmen, USt-VA). Nur für die Prognose — nie Teil der Ist-Zahlen.
      </p>
      <div className="mt-3">
        <PlannedItemForm initial={emptyDraft()} submitLabel="Hinzufügen"
          pending={create.isPending}
          onSubmit={(b) => create.mutateAsync(b).then(() => true, () => false)}
        />
      </div>
      {list.isLoading ? (
        <LoadingSpinner />
      ) : (list.data ?? []).length === 0 ? (
        <p className="mt-3 text-sm text-white/60">Noch keine geplanten Positionen.</p>
      ) : (
        <ul className="mt-3 divide-y divide-white/10">
          {(list.data ?? []).map((item) => (
            <li key={item.id} className="py-2" data-testid="planned-item">
              {editing?.id === item.id ? (
                <PlannedItemForm
                  initial={{
                    plannedDate: item.plannedDate,
                    amount: centsToInput(item.amountCents),
                    direction: item.amountCents < 0 ? 'out' : 'in',
                    label: item.label,
                    counterparty: item.counterparty ?? '',
                  }}
                  submitLabel="Speichern"
                  pending={update.isPending}
                  onSubmit={(body) => update.mutateAsync({ item: editing, body }).then(() => true, () => false)}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="min-w-0 break-words">
                    {day(item.plannedDate)} · {item.label}
                    {item.counterparty ? ` · ${item.counterparty}` : ''} · <strong>{money(item.amountCents)}</strong>
                  </span>
                  <span className="flex gap-2">
                    <Button onClick={() => setEditing(item)}>Bearbeiten</Button>
                    <Button
                      disabled={remove.isPending}
                      onClick={() => {
                        if (globalThis.window.confirm(`„${item.label}" löschen?`)) remove.mutate(item.id);
                      }}
                    >
                      Löschen
                    </Button>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </GlassPanel>
  );
}

export function ForecastTab({ tenantId, onNotice }: BankTabProps) {
  const [horizonDays, setHorizonDays] = useState<number>(90);
  const [includeExpected, setIncludeExpected] = useState(true);
  const [includeLongOverdue, setIncludeLongOverdue] = useState(false);
  const forecast = useQuery({
    queryKey: bankKeys.forecast(tenantId, horizonDays, includeExpected, includeLongOverdue),
    queryFn: () => bankApi.forecast({ horizonDays, includeExpected, includeLongOverdue }),
    enabled: Boolean(tenantId),
  });
  return (
    <div className="space-y-4">
      <GlassPanel className="bank-no-print">
        <h2 className="text-lg font-semibold">Liquiditätsprognose</h2>
        <p className="mt-1 text-sm text-white/70">
          Deterministische Fortschreibung des belegten Kontostands: wiederkehrende Lastschriften (geschätzt), offene
          Ausgangsrechnungen zum Fälligkeitstag (erwartet) und geplante Positionen. Keine Ist-Zahlen, keine steuerliche Aussage.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <label className="text-sm">
            Zeitraum
            <select
              aria-label="Prognosezeitraum"
              className="block rounded border border-white/10 bg-surface p-2"
              value={horizonDays}
              onChange={(e) => setHorizonDays(Number(e.target.value))}
            >
              {HORIZONS.map((h) => (
                <option key={h} value={h}>
                  {h} Tage
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <input
              type="checkbox"
              aria-label="Erwartete Zahlungseingänge einrechnen"
              checked={includeExpected}
              onChange={(e) => setIncludeExpected(e.target.checked)}
            />{' '}
            Erwartete Zahlungseingänge (offene Rechnungen) einrechnen
          </label>
          <label className="text-sm">
            <input
              type="checkbox"
              aria-label="Lange überfällige Rechnungen einrechnen"
              checked={includeLongOverdue}
              disabled={!includeExpected}
              onChange={(e) => setIncludeLongOverdue(e.target.checked)}
            />{' '}
            Auch Rechnungen einrechnen, die mehr als 60 Tage überfällig sind
          </label>
        </div>
      </GlassPanel>
      {forecast.isLoading ? (
        <LoadingSpinner />
      ) : forecast.error ? (
        <p role="alert">{errorText(forecast.error)}</p>
      ) : forecast.data ? (
        forecast.data.ok ? (
          <>
            <Projection data={forecast.data} />
            <RecurringGroups data={forecast.data} onNotice={onNotice} />
          </>
        ) : (
          <Refused data={forecast.data} />
        )
      ) : null}
      <PlannedItems tenantId={tenantId} onNotice={onNotice} />
    </div>
  );
}
